export interface OverlayElement {
    image: HTMLImageElement;
    x: number;
    y: number;
    width: number;
    height: number;
    type: 'qr' | 'logo' | 'extra';
}

export interface CompositionOptions {
    baseImage: HTMLImageElement;
    overlays: OverlayElement[];
    width: number;
    height: number;
}

export interface ExportOptions {
    type?: 'image/png' | 'image/jpeg';
    filename?: string;
    quality?: number; // Solo para JPEG (0.0 - 1.0)
}

/**
 * Carga una imagen desde URL/path
 * @param src URL o path de la imagen
 * @returns Promise<HTMLImageElement>
 */
export function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Importante para evitar canvas tainting
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
        // Some bundled assets in this repo include spaces in filenames.
        // Encoding ensures consistent runtime loading across browsers.
        img.src = encodeURI(src);
    });
}

/**
 * Renderiza la composición final en un canvas
 * @param options Opciones de composición
 * @returns HTMLCanvasElement con la composición renderizada
 */
export function renderComposition(options: CompositionOptions): HTMLCanvasElement {
    const { baseImage, overlays, width, height } = options;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('No se pudo obtener contexto 2D del canvas');
    }

    // Dibujar imagen base
    ctx.drawImage(baseImage, 0, 0, width, height);

    // Dibujar overlays en orden
    overlays.forEach(overlay => {
        ctx.drawImage(
            overlay.image,
            overlay.x,
            overlay.y,
            overlay.width,
            overlay.height
        );
    });

    return canvas;
}

/**
 * Exporta un canvas como archivo descargable
 * @param canvas Canvas a exportar
 * @param options Opciones de exportación
 */
export async function exportCanvas(
    canvas: HTMLCanvasElement,
    options: ExportOptions = {}
): Promise<void> {
    const {
        type = 'image/png',
        filename = 'creative',
        quality = 0.92
    } = options;

    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('No se pudo generar el blob de la imagen'));
                    return;
                }

                // Crear link temporal y descargar
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${filename}.${type === 'image/png' ? 'png' : 'jpg'}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Limpiar
                setTimeout(() => URL.revokeObjectURL(url), 100);
                resolve();
            },
            type,
            quality
        );
    });
}

/**
 * Calcula las dimensiones manteniendo aspect ratio
 */
export function calculateAspectRatio(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number
): { width: number; height: number } {
    const ratio = Math.min(maxWidth / originalWidth, maxHeight / originalHeight);
    return {
        width: originalWidth * ratio,
        height: originalHeight * ratio
    };
}

/**
 * Clamp value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

/**
 * Calcula posición por defecto del QR (bottom-right con padding)
 */
export function getDefaultQrPosition(
    canvasWidth: number,
    canvasHeight: number,
    qrSize: number,
    padding: number = 48
): { x: number; y: number } {
    return {
        x: canvasWidth - qrSize - padding,
        y: canvasHeight - qrSize - padding
    };
}

/**
 * Calcula posición por defecto del logo (top-right con padding)
 */
export function getDefaultLogoPosition(
    canvasWidth: number,
    canvasHeight: number,
    logoWidth: number,
    logoHeight: number,
    padding: number = 48
): { x: number; y: number } {
    return {
        x: canvasWidth - logoWidth - padding,
        y: padding
    };
}

/**
 * Calcula el tamaño recomendado del QR (20% del lado corto)
 */
export function getRecommendedQrSize(
    canvasWidth: number,
    canvasHeight: number
): number {
    const shortSide = Math.min(canvasWidth, canvasHeight);
    return Math.floor(shortSide * 0.2);
}
